import { useAtomValue, useSetAtom } from "jotai";
import pluralize from "pluralize";
import { Alert, Button } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import { Modal, SelectPicker } from "rsuite";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import AppHelmet from "../components/AppComponents/AppHelmet";
import useSharedRecord, {
  shareRecordPasswordAtom,
} from "../api/api-hooks/sharedRecords/useSharedRecord";
import { RecordSummaryTab } from "../components/Drawers/Records/ViewRecordDrawer";
import { ScreenSizeAtom } from "../App";
import { LayoutAndSections, Module } from "../types/TenantInterfaces";
import AppSpinner from "../elements/AppSpinner";
import { hideToast, newToastEvent } from "../utils/ToastUtil";
import { CloudApp, SharedRecordsField } from "../types/GlobalInterfaces";
import useTenantsWithApp from "../api/api-hooks/sharedRecords/useTenantsWithApp";
import useUser from "../api/api-hooks/auth/useUser";
import useAddSharedRecord from "../api/api-hooks/sharedRecords/useAddSharedRecord";
import useUpdateRecordWithShared from "../api/api-hooks/sharedRecords/useUpdateRecordWithShared";
import MergeItemsDrawer from "../components/Drawers/MergeItemsDrawer";
import useMergeRecordWithShared from "../api/api-hooks/sharedRecords/useMergeRecordWithShared";
import useCloud from "../api/api-hooks/cloud/useCloud";
import PasswordProtectedView from "./SharedViewLayout/PasswordProtectedView";
import SharedRecordAPI from "../api/api/SharedRecordAPI";
import { useQueryClient } from "@tanstack/react-query";
import FieldUtils from "../utils/FieldUtils";
import NotFoundView from "./SharedViewLayout/NotFoundView";

const UserProtectedPage = ({ children }: { children: JSX.Element }) => {
  const { user, userLoading } = useUser(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (user || userLoading) return;

    newToastEvent("error", "Please login to access that page.");
    navigate(`/login?r=${window.location.href}`, { replace: true });
  }, [user, userLoading]);

  if (userLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100vh">
        <AppSpinner
          color="primary"
          style={{ fontSize: 30, height: 50, width: 50 }}
        />
      </div>
    );
  }

  return children;
};

const ShareRecordLayout = () => {
  const { sharedRecordID }: any = useParams<any>();
  const {
    record,
    moduleSchema,
    userName,
    loading,
    viewID,
    pageID,
    success,
    requiresPassword,
  } = useSharedRecord(sharedRecordID);
  const screenSize = useAtomValue(ScreenSizeAtom);
  const [addModal, setAddModal] = useState(false);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const setShareRecordPasswords = useSetAtom(shareRecordPasswordAtom);
  const client = useQueryClient();

  useEffect(() => {
    if (!userName) return;

    newToastEvent(
      "success",
      `${userName} shared a record with you`,
      "shareRecord",
      { autoClose: false }
    );

    return () => hideToast("shareRecord");
  }, [userName]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100vh">
        <AppSpinner
          color="primary"
          style={{ fontSize: 30, height: 50, width: 50 }}
        />
      </div>
    );
  }

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      if (
        err?.config?.url?.includes("/getSharedRecord/") &&
        err?.response?.status === 400 &&
        err?.response?.data === "Bad Request"
      ) {
        navigate(`/login?r=${window.location.href}`);
        newToastEvent("error", "Please login to access that page.");
      }

      return Promise.reject(err);
    }
  );

  if (!success && requiresPassword) {
    return (
      <PasswordProtectedView
        onSubmit={updatePassword}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (!success) return <NotFoundView />;

  return (
    <>
      <AppHelmet
        title={record?._RecordName}
        description={`${userName} shared a record with you`}
      />
      <div
        className="d-flex flex-column justify-content-center align-items-center px-2 px-sm-4 position-relative"
        style={{
          minHeight: "100vh",
          backgroundImage: "linear-gradient(180deg, #fff 0%, #E4FDEF 100%)",
          backgroundAttachment: "fixed",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="min-vh-100 bg-white px-sm-3 btn-oauth"
          style={{ width: "100%", maxWidth: 1000 }}
        >
          <div className="p-3 d-flex justify-content-between">
            <div>
              <div
                style={{ fontSize: 14 }}
                className="text-muted font-weight-500"
              >
                {moduleSchema?.Name && pluralize.singular(moduleSchema?.Name)}
              </div>
              <div className="d-flex align-items-center font-weight-600 text-dark">
                {record?._RecordName || "(No Name)"}
              </div>
            </div>
            <Button color="primary" onClick={() => setAddModal(true)}>
              Add To Cloud
            </Button>
          </div>
          <RecordSummaryTab
            module={moduleSchema!}
            record={record}
            layout={{ Default: true } as LayoutAndSections}
            setLayout={() => {}}
            screenSize={screenSize}
            setUpdateRecordDrawer={undefined}
          />
        </div>
      </div>
      <AddSharedRecordModal
        open={addModal}
        onClose={() => setAddModal(false)}
        sharedRecord={record}
        moduleSchema={moduleSchema!}
        viewID={viewID}
        pageID={pageID}
      />
    </>
  );

  async function updatePassword(password: string) {
    if (!password) return;

    setError("");
    setSubmitting(true);

    setShareRecordPasswords((prev) => ({
      ...prev,
      [`${sharedRecordID}`]: password,
    }));

    try {
      const result = await SharedRecordAPI.getSharedRecord(
        sharedRecordID,
        password
      );

      const data = result.data.data;

      if (data?.moduleSchema) {
        FieldUtils.addDefaultsToSchema(
          {
            CRM: { Modules: [data.moduleSchema] },
          },
          true
        );
      }

      if (data.success === false) {
        setError("Invalid password provided.");
      } else if (data.success === true) {
        client.setQueryData(["sharedRecord", sharedRecordID], data);
      }
    } catch (e) {
      console.log({ e });
      setError("An unexpected error");
    } finally {
      setSubmitting(false);
    }
  }
};

const ProtectedLayout = () => {
  return (
    <>
      <UserProtectedPage>
        <ShareRecordLayout />
      </UserProtectedPage>
    </>
  );
};

export default ProtectedLayout;

interface TenantInfo {
  _id: string;
  Name: string;
  AppVersion?: number;
  Valid: boolean;
}

function AddSharedRecordModal({
  open,
  onClose,
  sharedRecord,
  moduleSchema,
  viewID,
  pageID,
}: {
  open: boolean;
  onClose: () => void;
  sharedRecord: SharedRecordsField;
  moduleSchema: Module;
  viewID: string | undefined;
  pageID: string | undefined;
}) {
  const { sharedRecordID }: any = useParams<any>();
  const { tenants, loadingTenants, app } = useTenantsWithApp(sharedRecordID);
  const { addSharedRecord, addingRecord } = useAddSharedRecord();
  const [workspaceID, setWorkspaceID] = useState("");

  const [selectedCloud, setSelectedCloud] = useState<TenantInfo | null>(null);
  const [installAppAlert, setInstallAppAlert] = useState(false);
  const [existingRecord, setExistingRecord] = useState<any>(null);
  const [error, setError] = useState("");
  const [successID, setSuccessID] = useState("");

  let Body;

  if (successID && selectedCloud) {
    Body = (
      <AddedRecordAlert
        recordID={successID}
        tenantID={selectedCloud?._id}
        moduleAPIName={moduleSchema?.APIName}
        viewID={viewID}
        pageID={pageID}
        workspaceID={workspaceID}
      />
    );
  } else if (existingRecord && selectedCloud) {
    Body = (
      <RecordExists
        sharedRecord={sharedRecord}
        existingRecord={existingRecord}
        selectedCloud={selectedCloud}
        setSuccessID={setSuccessID}
        moduleSchema={moduleSchema}
      />
    );
  } else
    Body = (
      <div className="p-3">
        <div className="d-flex justify-content-end">
          <i
            className="mdi mdi-close text-dark text-lg cursor-pointer"
            onClick={handleClose}
          />
        </div>
        <h2 className="text-center mb-4 text-dark">Add Record To Cloud</h2>
        <h4 className="text-center">Select A Cloud</h4>
        <div className="d-flex justify-content-center w-100">
          <div
            className="d-flex align-items-center w-100 mb-4"
            style={{ maxWidth: 400 }}
          >
            <img
              src={"/img/GCLogo70x70.png"}
              style={{ height: 40, width: 40 }}
              className="mr-1"
            />
            <SelectPicker
              searchable={false}
              disabled={loadingTenants || addingRecord}
              placeholder={
                loadingTenants ? "Loading Clouds..." : "Select Cloud"
              }
              data={tenants ? tenants : []}
              labelKey="Name"
              valueKey="_id"
              style={{ flex: 1 }}
              value={selectedCloud?._id || ""}
              onChange={handleCloudSelection}
            />
          </div>
        </div>
        {selectedCloud?.Valid && !addingRecord && (
          <div className="d-flex justify-content-center mb-3 mt-2">
            <Button color="primary" onClick={handleAddRecord}>
              Add Record
            </Button>
          </div>
        )}
        {addingRecord && (
          <div className="d-flex flex-column align-items-center mt--2 mb-3">
            <h3>Record will be added to the Global App</h3>
            <div className="font-weight-600  text-lg text-dark mb-3">
              <img
                src={
                  typeof app.Icon === "string" ? app.Icon : app?.Icon?.PublicURL
                }
                style={{ height: 35, width: 35 }}
                className="mr-2"
              />
              {app.Name}
            </div>
            <AppSpinner
              color="primary"
              style={{ height: 50, width: 50, fontSize: 24 }}
            />
          </div>
        )}
        {!loadingTenants && !tenants.length && (
          <Alert
            color="danger"
            className="d-flex align-items-center gap-2 font-weight-bold"
          >
            <i className="mdi mdi-alert-circle" style={{ fontSize: 22 }} />
            You have no clouds that can download this Shared Record.
          </Alert>
        )}
        {error && (
          <Alert
            color="danger"
            className="d-flex align-items-center gap-2 font-weight-bold"
          >
            <i className="mdi mdi-alert-circle" style={{ fontSize: 22 }} />
            {error}
          </Alert>
        )}
      </div>
    );

  return (
    <>
      <Modal
        open={open || installAppAlert}
        onClose={installAppAlert ? undefined : handleClose}
      >
        {Body}
      </Modal>
      <InstallAppAlert
        open={installAppAlert}
        onClose={() => {
          setInstallAppAlert(false);
          setSelectedCloud(null);
        }}
        app={app}
        cloudID={selectedCloud?._id}
        cloudName={selectedCloud?.Name}
      />
    </>
  );

  function handleCloudSelection(cloudID: string | null) {
    if (!cloudID) return setSelectedCloud(null);

    const selectedCloud = tenants?.find((t: any) => t._id === cloudID);

    setSelectedCloud(selectedCloud || null);
    if (!selectedCloud.Valid) setInstallAppAlert(true);
  }

  // Add the record to cloud,
  function handleAddRecord() {
    if (!selectedCloud) return;

    setError("");

    addSharedRecord({
      cloudID: selectedCloud._id,
      sharedRecordID: sharedRecordID,
      onError: setError,
      onExists: setExistingRecord,
      onSuccess: (result) => {
        setSuccessID(result.recordID);
        setWorkspaceID(result.workspaceID);
      },
    });
  }

  function handleClose() {
    setSelectedCloud(null);
    setError("");
    setInstallAppAlert(false);
    setExistingRecord(null);
    setSuccessID("");
    setWorkspaceID("");
    onClose();
  }
}

function InstallAppAlert({
  open,
  onClose,
  app,
  cloudID,
  cloudName,
}: {
  open: boolean;
  onClose: () => void;
  app: CloudApp | undefined;
  cloudID: string | undefined;
  cloudName: string | undefined;
}) {
  const navigate = useNavigate();

  if (!app || !cloudID || !cloudName) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div
        className="text-lg d-flex justify-content-end pt-2 pr-3 cursor-pointer"
        onClick={onClose}
      >
        <i className="mdi mdi-close" />
      </div>
      <div className="d-flex flex-column align-items-center justify-content-center">
        <i
          className="mdi mdi-alert-circle-outline text-warning"
          style={{ fontSize: 52 }}
        />
        <h1 className="text-center text-dark" style={{ width: 450 }}>
          To save this record in {cloudName}, please install the latest version
          of
        </h1>
        <div className="tw:font-semibold tw:text-lg tw:text-dark tw:mt-3 tw:flex tw:items-center">
          <img
            src={typeof app.Icon === "string" ? app.Icon : app?.Icon?.PublicURL}
            style={{ height: 35, width: 35 }}
            className="mr-2"
          />
          {app.Name}
        </div>
        <div className="mt-5 mb-4 d-flex" style={{ gap: 20 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            onClick={() =>
              navigate(
                `/clouds/${cloudID}/activity?store=true&page=all-products`
              )
            }
          >
            Install Global App
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AddedRecordAlert({
  recordID,
  tenantID,
  moduleAPIName,
  viewID,
  pageID,
  workspaceID,
}: {
  recordID: string;
  tenantID: string;
  moduleAPIName: string;
  viewID: string | undefined;
  pageID: string | undefined;
  workspaceID: string;
}) {
  const { cloud, cloudLoading } = useCloud(tenantID);
  const navigate = useNavigate();

  if (cloudLoading || !cloud) {
    return (
      <div className="py-6 d-flex justify-content-center">
        <AppSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center">
      <i
        className="mdi mdi-check-circle text-primary mt-3"
        style={{ fontSize: 40 }}
      />
      <h1>Record Successfully Added</h1>
      <div>This record has been successfully saved to your cloud</div>
      <Button
        className="my-4"
        color="primary"
        onClick={() => {
          let link;

          if (pageID && viewID && workspaceID) {
            const params = new URLSearchParams();
            params.set(
              "open",
              JSON.stringify([
                {
                  vID: viewID,
                  id: recordID,
                  module: {
                    APIName: moduleAPIName,
                    Name: moduleAPIName,
                  },
                  tab: "Summary",
                },
              ])
            );

            link = `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${pageID}/views/${viewID}?${params.toString()}`;
          } else {
            link = `/clouds/${tenantID}/records/${moduleAPIName}/details/${recordID}/Summary`;
          }

          navigate(link);
        }}
      >
        View Record
      </Button>
    </div>
  );
}

function RecordExists({
  sharedRecord,
  existingRecord,
  selectedCloud,
  setSuccessID,
  moduleSchema,
}: {
  sharedRecord: any;
  existingRecord: any;
  selectedCloud: TenantInfo;
  setSuccessID: React.Dispatch<React.SetStateAction<string>>;
  moduleSchema: Module;
}) {
  const { sharedRecordID }: any = useParams<any>();
  const [update, setUpdate] = useState(false);
  const [merge, setMerge] = useState(false);
  const [error, setError] = useState("");
  const { updateRecordWithShared, updatingRecord } =
    useUpdateRecordWithShared();
  const { mergeRecordWithShared, mergingRecord } = useMergeRecordWithShared();
  const navigate = useNavigate();

  const customSharedRecord = useMemo(() => {
    const r = structuredClone(sharedRecord);
    r.id = sharedRecordID;
    r._id = sharedRecordID;
    r._RecordName = r._RecordName + " (Shared)";
    return r;
  }, [sharedRecord]);

  const customExistingRecord = useMemo(() => {
    const r = structuredClone(existingRecord);
    r._RecordName = r._RecordName + ` (${selectedCloud.Name} Cloud)`;
    return r;
  }, [existingRecord]);

  const fieldMap = useMemo(() => {
    const map: { [key: string]: any } = {};
    const fields = moduleSchema?.Fields;

    if (!fields) return map;

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];

      map[field.APIName] = field;
    }

    return map;
  }, [moduleSchema]);

  if (update)
    return (
      <div className="d-flex flex-column align-items-center justify-content-center px-3 px-sm-5">
        <i
          className="mdi mdi-alert-circle-outline text-warning"
          style={{ fontSize: 52 }}
        />
        <h1 className="text-center text-dark" style={{ width: 450 }}>
          Updating Record
        </h1>
        <div className="mt-2 text-center">
          Are you sure you want to update the existing record in your cloud with
          this shared record? All conflicting fields in your existing record
          will automatically be overrwritten with the fields from this shared
          record. To choose what fields you want to keep, cancel and choose
          "Merge"
        </div>
        <div className="mt-4 mb-4 d-flex" style={{ gap: 20 }}>
          <Button onClick={() => setUpdate(false)}>Cancel</Button>
          <Button color="primary" onClick={handleUpdateRecord}>
            {updatingRecord ? <AppSpinner color="white" size="sm" /> : "Update"}
          </Button>
        </div>
        {error && (
          <Alert color="danger" className="p-2">
            {error}
          </Alert>
        )}
      </div>
    );

  return (
    <>
      <div className="d-flex flex-column align-items-center py-4">
        <div style={{ width: "100%", maxWidth: 430 }}>
          <h1 className="text-center">Record Already Exists</h1>
          <div className="text-center text-sm mt-2">
            The record you are trying to add already exists in your cloud.
            <br /> Select which action you want to perform
          </div>
          <div className="d-flex justify-content-between text-dark font-weight-600 align-items-center mt-3">
            {existingRecord?._RecordName}
            <div className="d-flex">
              <IconAction
                onClick={() => setUpdate(true)}
                icon="file-replace-outline"
                title="Replace"
              />
              <IconAction
                onClick={() => setMerge(true)}
                icon="merge"
                title="Merge"
              />
            </div>
          </div>
          <div className="d-flex justify-content-center mt-5 mb-2">
            <Button onClick={() => navigate("/")}>
              Skip and go to GrowthCloud
            </Button>
          </div>
        </div>
      </div>
      <MergeItemsDrawer
        itemOne={customSharedRecord}
        itemTwo={customExistingRecord}
        open={merge}
        onClose={() => setMerge(false)}
        onMerge={({ payload, onSuccess }: any) => {
          mergeRecordWithShared({
            sharedRecordID: sharedRecordID,
            cloudID: selectedCloud._id,
            payload: payload.ItemPayload,
            onSuccess: (recordID) => {
              setSuccessID(recordID);
              onSuccess();
            },
            onError: setError,
          });
        }}
        module={moduleSchema}
        loading={false}
        submitting={mergingRecord}
        fieldMap={fieldMap}
        error={error}
        isSubitemMerge={false}
        hideID={true}
        disableViewRecord={true}
      />
    </>
  );

  function handleUpdateRecord() {
    updateRecordWithShared({
      sharedRecordID: sharedRecordID,
      cloudID: selectedCloud._id,
      onSuccess: setSuccessID,
      onError: setError,
    });
  }
}

function IconAction({
  onClick,
  icon,
  title,
}: {
  onClick: () => void;
  icon: string;
  title?: string;
}) {
  return (
    <div
      onClick={onClick}
      className="d-flex flex-column align-items-center px-2 py-1 mx-1 my-1 bg-primary--hover cursor-pointer rounded"
    >
      <i
        className={`mdi mdi-${icon}`}
        style={{ fontSize: 24, lineHeight: 1 }}
      />
      <span className="font-weight-500" style={{ fontSize: 14 }}>
        {title}
      </span>
    </div>
  );
}
